import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://gormaran-growth-hub-2.onrender.com';

/**
 * Real-time drafts for the current user.
 * Returns: { drafts, loading, approve, discard, adjust }
 * Subscribes to users/{uid}/drafts via Firestore onSnapshot.
 */
export function useDrafts({ clientId, estado } = {}) {
  const { currentUser } = useAuth();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) { setLoading(false); return; }

    let q = query(
      collection(db, 'users', currentUser.uid, 'drafts'),
      orderBy('createdAt', 'desc'),
      limit(50),
    );
    if (clientId) q = query(q, where('clientId', '==', clientId));
    if (estado)   q = query(q, where('estado',   '==', estado));

    const unsub = onSnapshot(q, (snap) => {
      setDrafts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    return unsub;
  }, [currentUser?.uid, clientId, estado]);

  async function apiCall(path, body) {
    const token = await currentUser.getIdToken();
    const r = await fetch(`${API}/api/drafts${path}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const data = await r.json().catch(() => ({}));
      throw new Error(data.error || `${r.status}`);
    }
    return r.json();
  }

  const approve       = useCallback((id)              => apiCall(`/${id}/approve`,   {}),               [currentUser]);
  const discard       = useCallback((id, razon)       => apiCall(`/${id}/discard`,  { razon }),          [currentUser]);
  const adjust        = useCallback((id, instruccion) => apiCall(`/${id}/adjust`,   { instruccion }),    [currentUser]);
  const markPublished = useCallback((id, canal)       => apiCall(`/${id}/publish`,  { canal }),          [currentUser]);
  const schedule      = useCallback((id, scheduledAt, canal) => apiCall(`/${id}/schedule`, { scheduledAt, canal }), [currentUser]);
  const unschedule    = useCallback((id)              => apiCall(`/${id}/unschedule`, {}),               [currentUser]);

  return { drafts, loading, approve, discard, adjust, markPublished, schedule, unschedule };
}
